import intersection from 'lodash-es/intersection'
import { InjectionKey } from 'vue'
import { LocationQueryValue, useRoute, useRouter } from 'vue-router'

/**
 * QUERY PARAM SETS
 * Each set should only have one valid entry at a time
 * (or a set of valid entries all added at the same time)
 * when updating items from a set,
 * delete all other existing set entries
 */

// Patient Sidebar
const sidebarQueryParams = [
  'commId',
  'editing',
  'subtaskId',
  'referralIds',
  'visitNoteId',
  'isVisitSummary',
  'pathwayId',
  'taskId',
]

// Texting inbox
// used in UnknownText page and PatientProfile
const textingTabQueryParams = ['smsCommId', 'phoneNumberId']

const ALL_QUERY_SETS = [sidebarQueryParams, textingTabQueryParams]

type queryType = {
  [x: string]: LocationQueryValue | LocationQueryValue[] | undefined
}

// https://vuejs.org/guide/typescript/composition-api#typing-provide-inject
export const SET_QUERY_KEY = Symbol('SET_QUERY_KEY') as InjectionKey<
  (query: Record<string, string | undefined>) => Promise<void>
>

/**
 *
 */
export function setupRouter() {
  const route = useRoute()
  const router = useRouter()

  /**
   *
   * @param newQuery
   */
  async function setQuery(newQuery: Record<string, string | undefined>) {
    let buildQuery: queryType = { ...route.query }

    // clear all other params from any set that has a param being updated
    for (const querySet of ALL_QUERY_SETS) {
      if (intersection(querySet, Object.keys(newQuery)).length > 0) {
        buildQuery = {
          ...buildQuery,
          ...querySet.reduce(
            (acc, param) => {
              acc[param] = undefined
              return acc
            },
            {} as Record<string, undefined>
          ),
        }
      }
    }

    // overwrite with new params
    const updatedQuery: queryType = {
      ...buildQuery,
      ...newQuery,
    }

    await router.push({ query: updatedQuery })
  }

  return {
    setQuery,
  }
}

/**
 * Extract patient ID from a route path
 * @param path - Route path like /patient/123 or /patient/123/pathways
 * @returns Patient ID or null if not a patient path
 */
export function getPatientIdFromPath(path: string): string | null {
  const match = path.match(/^\/patient\/([^/]+)/)
  return match ? match[1] : null
}
